import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import axios from 'axios';
import config from '../../config.js';
import SubTitle from '../common/SubTitle';
import RequestTable from './RequestTable';
import RequestFilter from './RequestFilter';
import { useNavigate } from 'react-router-dom';
import RequestSubTitle from './RequestSubTitle';
import RejectModal from './RejectModal';
import Footer from '../common/Footer';
// Approval Message Component
function ApprovalMessage({ open, onClose }) {
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        onClose();
      }, 1000); // 1 second delay
      return () => clearTimeout(timer);
    }
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '16px',
        p: 3,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
      }}
    >
      <CircularProgress size={30} sx={{ mb: 2, color: '#183059' }} />
      <Typography variant="h6" sx={{ color: '#183059', fontWeight: 500 }}>
        Organization Approved!
      </Typography>
      <Typography variant="body2" sx={{ color: '#666', textAlign: 'center' }}>
        The organization has been successfully approved.
      </Typography>
    </Box>
  );
}

export default function AdminDashboard() {
  const [organizations, setOrganizations] = useState([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const navigate = useNavigate();
  const [orgData, setOrgData] = useState([]);
  const [showApprovalMessage, setShowApprovalMessage] = useState(false);
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [showRejected, setShowRejected] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false)

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [orgIdToReject, setOrgIdToReject] = useState(null);
  const [orgNameToReject, setOrgNameToReject] = useState('');


  // Unique countries and cities
  const organization = [...new Set(organizations.map(org => org.name).filter(Boolean))];
  const cities = [...new Set(organizations.map(org => org.city).filter(Boolean))];

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    filterOrganizations();
  }, [organizations, searchTerm, countryFilter, cityFilter, showRejected]);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.baseURL}/register`, {
        withCredentials: true,
      });

      // Filter out accepted organizations here
      const filteredData = response.data.organizations.filter(org =>
        org.approvalStatus !== 'ACCEPTED'
      );

      const transformedOrganizations = filteredData.map(org => ({
        id: org.id.toString(),
        name: org.name,
        country: org.country || 'Not Specified',
        city: org.city || 'Not Specified',
        contact: org.contactName || 'Not Specified',
        email: org.contactEmail || 'Not Specified',
        phoneNumber: org.contactPhone || 'Not Specified',
        approvalStatus: org.approvalStatus || 'PENDING',
      }));

      // Store filtered orgData as well
      setOrgData(filteredData);
      setOrganizations(transformedOrganizations);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setError(error.response?.data?.msg || 'Failed to fetch organizations');
      setLoading(false);
    }
  };

  const filterOrganizations = () => {
    let filtered = organizations;

    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(org =>
        Object.values(org).some(value =>
          String(value).toLowerCase().includes(searchTermLower)
        )
      );
    }

    if (countryFilter) {
      filtered = filtered.filter(org => org.name === countryFilter);
    }

    if (cityFilter) {
      filtered = filtered.filter(org => org.city === cityFilter);
    }

    if (showRejected) {
      filtered = filtered.filter(org => org.approvalStatus === 'REJECTED');
    } else {
      // When not showing only rejected, show both PENDING and REJECTED
      filtered = filtered.filter(org =>
        org.approvalStatus === 'PENDING' || org.approvalStatus === 'REJECTED'
      );
    }

    setFilteredOrganizations(filtered);
  };

  const handleApprove = async (id) => {
    try {
      await axios.post(`${config.baseURL}/register/${id}/accept`, {}, {
        withCredentials: true,
      });
      setShowApprovalMessage(true); // Show approval message
      fetchOrganizations(); // Refresh the list
    } catch (error) {
      console.error('Error approving organization:', error);
      setError(error.response?.data?.msg || 'Failed to approve organization');
    }
  };

  // const handleReject = async (id) => {
  //   try {
  //     await axios.post(`${config.baseURL}/register/${id}/reject`, {}, {
  //       withCredentials: true,
  //     });
  //     alert('Organization rejected successfully!');
  //     fetchOrganizations();
  //   } catch (error) {
  //     console.error('Error rejecting organization:', error);
  //     setError(error.response?.data?.msg || 'Failed to reject organization');
  //   }
  // };
  const handleReject = (id) => {
    const orgToReject = organizations.find(org => org.id === id.toString());
    setOrgIdToReject(id);
    setOrgNameToReject(orgToReject?.name || '');
    setRejectModalOpen(true);
  };

  const confirmReject = async (reason) => {
    setIsActionLoading(true)
    try {
      await axios.post(`${config.baseURL}/register/${orgIdToReject}/reject`,
        { reason: reason },
        { withCredentials: true }
      );
      setRejectModalOpen(false);
      // alert('Organization rejected successfully!');
      fetchOrganizations();
    } catch (error) {
      console.error('Error rejecting organization:', error);
      setError(error.response?.data?.msg || 'Failed to reject organization');
      setRejectModalOpen(false);
    } finally {
      setIsActionLoading(false)
    }
  };

  const handleView = (org) => {
    const fullOrgDetails = orgData.find(fullOrg => fullOrg.id.toString() === org.id);
    console.log(fullOrgDetails, "fullOrgDetails")
    navigate(`/request/view/${fullOrgDetails.organizationId}`, {
      state: { selectedOrganization: fullOrgDetails },
    });
  };

  const handleCloseApprovalMessage = () => {
    setShowApprovalMessage(false);
  };

  const handleCreateOrg = () => {
    navigate("/organizationUpdate")
  }

 const orgLength = organizations.filter(org => org.approvalStatus === 'PENDING').length;

  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization?.name;

  if (loading) {
    return (
      <>
        <SubTitle 
          title="newRequest" 
          path={`${orgName || "organization"} > Organizations > New Requests`} 
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress size={50} sx={{ color: '#183059' }} />
        </Box>
      </>
    )
  }

  if (error) {
    return (
      <>
        <SubTitle 
          title="newRequest" 
          path={`${orgName || "organization"} > Organizations > New Requests`} 
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Typography variant="h6" color="error">{error}</Typography>
        </Box>
      </>
    )
  }

  return (
    <>
      <RequestSubTitle
        title="newRequest"
        path={`${orgName || "organization"} > Organizations > New Requests`}
        cardtitle="Total Request "
        cardValue={orgLength}
        buttonText="+ Create Organization"
        onButtonClick={handleCreateOrg}
      />
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mx: 5, p: 2 }}>
        <RequestFilter
          organizations={organization}
          cities={cities}
          onSearchChange={setSearchTerm}
          onCountryChange={setCountryFilter}
          onCityChange={setCityFilter}
          onToggleRejected={setShowRejected}
        />
        <RequestTable
          organizations={filteredOrganizations}
          loading={loading}
          onApprove={handleApprove}
          onReject={handleReject}
          onView={handleView}
          showRejected={showRejected}
        />
      </Box>
      {/* <ApprovalMessage open={showApprovalMessage} onClose={handleCloseApprovalMessage} /> */}
      <Box sx={{ height: "20px" }}></Box>
      <RejectModal
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={confirmReject}
        isActionLoading={isActionLoading}
        organizationName={orgNameToReject}
      />
      <Footer/>
    </>
  );
}
